/**
 * Header Component
 *
 * Site header with SZU blue background, navigation, and language switcher.
 * Follows the SZU design system specification.
 */

import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Beaker, Menu, X, Search } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { cn } from '@/lib/utils';

/**
 * Navigation link type
 */
interface NavLinkItem {
  to: string;
  labelKey: 'nav.catalog' | 'nav.growthMedia' | 'nav.about' | 'nav.contact' | 'nav.ordering';
}

/**
 * Header component with SZU blue background and responsive navigation
 */
export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();

  /**
   * Navigation links with translation keys
   */
  const navLinks: NavLinkItem[] = [
    { to: '/catalog', labelKey: 'nav.catalog' },
    { to: '/growth-media', labelKey: 'nav.growthMedia' },
    { to: '/about', labelKey: 'nav.about' },
    { to: '/ordering', labelKey: 'nav.ordering' },
    { to: '/contact', labelKey: 'nav.contact' },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-primary shadow-md">
      {/* Main header bar */}
      <div className="container mx-auto flex h-16 items-center justify-between px-4 lg:px-6">
        {/* Logo and site name */}
        <Link
          to="/"
          className="flex items-center gap-3 text-white transition-opacity hover:opacity-90"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10">
            <Beaker className="h-6 w-6 text-white" aria-hidden="true" />
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-bold leading-tight tracking-tight">
              {t('header.siteName')}
            </span>
            <span className="hidden text-xs text-white/80 sm:block">
              {t('header.siteDescription')}
            </span>
          </div>
        </Link>

        {/* Desktop navigation */}
        <nav className="hidden items-center gap-1 md:flex" aria-label="Main navigation">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                cn(
                  'rounded-md px-4 py-2 text-sm font-medium transition-all',
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'text-white/90 hover:bg-white/10 hover:text-white'
                )
              }
            >
              {t(link.labelKey)}
            </NavLink>
          ))}

          {/* Search link */}
          <Link
            to="/catalog"
            className="ml-2 flex h-9 w-9 items-center justify-center rounded-md text-white/90 transition-colors hover:bg-white/10 hover:text-white"
            aria-label={t('common.search')}
          >
            <Search className="h-5 w-5" aria-hidden="true" />
          </Link>

          {/* Divider */}
          <div className="mx-3 h-6 w-px bg-white/20" aria-hidden="true" />

          {/* Language switcher */}
          <LanguageSwitcher variant="header" />
        </nav>

        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/10 hover:text-white md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label={mobileMenuOpen ? t('common.close') : t('common.menu')}
        >
          {mobileMenuOpen ? (
            <X className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Menu className="h-6 w-6" aria-hidden="true" />
          )}
        </Button>
      </div>

      {/* Mobile navigation */}
      {mobileMenuOpen && (
        <nav
          id="mobile-menu"
          className="border-t border-white/10 bg-primary-600 md:hidden"
          aria-label="Mobile navigation"
        >
          <div className="container mx-auto space-y-1 px-4 py-4">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'block rounded-md px-4 py-3 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                  )
                }
              >
                {t(link.labelKey)}
              </NavLink>
            ))}

            {/* Mobile search link */}
            <Link
              to="/catalog"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-4 py-3 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Search className="h-4 w-4" aria-hidden="true" />
              {t('common.search')}
            </Link>

            {/* Divider */}
            <div className="my-2 border-t border-white/10" aria-hidden="true" />

            {/* Language switcher */}
            <div className="px-4 py-2">
              <LanguageSwitcher variant="header" showNames />
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}

export default Header;
